import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDropdown } from '../dropdown';
import s from './button.module.css';
export const context = createContext({
    select: void 0,
    info: {},
    mode: 'single',
    dataTest: void 0,
    native: false,
    nativeValue: null,
    onChange: () => { },
    registerInfoControl: () => { },
    registerSearchInput: () => { },
    registerSelect: () => { },
    search: {},
    setNativeValue: () => { },
    showInfo: () => { },
    showSearch: () => { },
});
export function useSelectedText(values = [], texts = []) {
    const textMap = useMemo(() => (texts || []).reduce((res, { value, text }) => {
        res.set(value, text);
        return res;
    }, new Map()), [texts]);
    const selectedText = useMemo(() => [...textMap.keys()]
        .reduce((res, value) => (values.includes(value) ? [...res, textMap.get(value)] : res), [])
        .join(', '), [textMap, values]);
    return selectedText;
}
export function useSelect(onChange, { selected, texts, } = {}) {
    const [value, setValue] = useState(selected);
    const handleChange = useCallback((value, _, evt) => {
        setValue(value);
        onChange(value, evt);
    }, [onChange]);
    useEffect(() => {
        setValue(selected);
    }, [selected]);
    const is = useCallback((check) => value === check, [value]);
    const selectedText = useSelectedText(typeof value !== 'undefined' ? [value] : [], texts);
    return {
        is,
        handleChange,
        selectedText,
    };
}
export function convertToValuesMap(values) {
    const result = new Map();
    values.forEach(item => result.set(item, true));
    return result;
}
export function useMultipleSelect(onChange, { selected = [], texts = [], } = {}) {
    const [values, setValue] = useState(convertToValuesMap(selected));
    const selectedText = useSelectedText([...values.keys()].filter(k => values.get(k)), texts);
    useEffect(() => {
        setValue(convertToValuesMap(selected));
    }, [JSON.stringify(selected)]);
    const handleChange = useCallback((value, checked, evt) => setValue(prev => {
        const update = new Map(prev).set(value, checked);
        onChange([...update.keys()].filter(k => update.get(k)), evt);
        return update;
    }), [onChange]);
    const is = useCallback((value) => !!values.get(value), [values]);
    return {
        is,
        handleChange,
        selectedText,
    };
}
export function useNativeSelect() {
    const select = useRef(null);
    const { registerSelect, onChange, native, nativeValue } = useContext(context);
    const handleChange = useCallback(event => {
        const int = 'int' in event.target.options[event.target.selectedIndex].dataset;
        if (onChange) {
            onChange(int ? parseFloat(event.target.value) : event.target.value, true, event);
        }
    }, [onChange]);
    useEffect(() => {
        if (!native || !select.current) {
            return;
        }
        registerSelect(select.current);
    }, [native, registerSelect]);
    useEffect(() => {
        if (!select.current || !native || !nativeValue) {
            return;
        }
        select.current.value = nativeValue;
    }, [native, nativeValue]);
    return {
        select,
        native,
        handleChange,
        style: s.native,
    };
}
export function useSelectCustomControl() {
    const { toggle, registerControl, opened, content } = useDropdown();
    const control = useRef(null);
    useEffect(() => {
        if (!control.current) {
            return;
        }
        registerControl(control.current);
    }, [registerControl]);
    return {
        toggle,
        control,
        content,
        opened,
    };
}
//# sourceMappingURL=utils.js.map